import { createAsyncThunk } from "@reduxjs/toolkit";

import { reverse } from "@reactivated";

import * as checkoutAPI from "../../../api/checkout";
import * as cybersourceAPI from "../../../api/cybersource";
import { OrderStatus, PaymentState } from "../../../constants";
import { strings } from "../../../localization";
import {
    ICybersourceTemplateContext,
    IPaymentRejectMsgs,
    IPaymentState,
} from "../../../models/checkout";
import { isoWebPageURL } from "../../../models/nominals";
import { IUser } from "../../../models/user";
import { objectKeys } from "../../../utils/functional";
import { navigateToURL } from "../../../utils/urls";
import { PRIMARY_PAYMENT_METHOD_KEY } from "../constants";
import { IPayment, IReduxState } from "../reducers.interfaces";
import { ICheckoutErrorBody, ICheckoutHTTPError } from "../types";

const PAYMENT_REJECT_MSGS: IPaymentRejectMsgs = {
    "cybersource": strings.get("REJECT_MSG_CYBERSOURCE") || "",
    "bluefin": strings.get("REJECT_MSG_CYBERSOURCE") || "",
    "financing": strings.get("REJECT_MSG_FINANCING") || "",
    "cash": strings.get("REJECT_MSG_CASH") || "",
    "pay-later": strings.get("REJECT_MSG_CASH") || "",
};

/**
 * Arguments for completeOrderPayment thunk.
 */
interface CompleteOrderPaymentArgs {
    user: IUser | null;
}

/**
 * Build checkout error object.
 */
const buildCheckoutError = (
    errorKey: keyof ICheckoutErrorBody,
    msg: string,
): ICheckoutHTTPError => {
    const error: ICheckoutHTTPError = Object.assign(new Error(msg), {
        response: {
            body: {
                [errorKey]: [msg],
            },
        },
    });
    return error;
};

/**
 * Navigate to the thank you page.
 */
const forwardToThankYouPage = (): void => {
    navigateToURL(isoWebPageURL.wrap(reverse("checkout:thank-you")));
};

/**
 * Get payment handlers for processing payment states.
 */
const getPaymentHandlers = (state: IReduxState, user: IUser | null) => {
    type IHandlers = {
        [t in IPayment["method_type"]]: (
            methodKey: string,
            paymentState: IPaymentState,
        ) => void;
    };
    const handlers: IHandlers = {
        "default": () => {
            throw buildCheckoutError(
                "payment",
                "Default method should never be pending",
            );
        },
        "cash": () => {
            throw buildCheckoutError(
                "payment",
                "Cash method should never be pending",
            );
        },
        "pay-later": () => {
            throw buildCheckoutError(
                "payment",
                "Pay Later method should never be pending",
            );
        },
        "financing": () => {
            throw buildCheckoutError(
                "payment",
                "Financing should never be pending",
            );
        },
        "cybersource": (methodKey, paymentState) => {
            const method = state.form.payment_methods[methodKey];
            if (!paymentState.required_action) {
                return;
            }
            if (method.method_type !== "cybersource") {
                throw new Error("Payment error occurred");
            }
            const context: ICybersourceTemplateContext = {
                card_type: method.card_type,
                card_number: method.card_number,
                card_expiration: method.card_expiration,
                card_cvc: method.card_cvc,
                email: user ? user.email : state.form.guest_email,
                billing_first_name: state.form.billing_first_name,
                billing_last_name: state.form.billing_last_name,
                billing_phone_number: state.form.billing_phone_number,
                billing_line1: state.form.billing_line1,
                billing_line2: state.form.billing_line2,
                billing_line4: state.form.billing_line4,
                billing_postcode: state.form.billing_postcode,
                billing_state: state.form.billing_state,
                billing_country: state.form.billing_country,
            };
            const request = cybersourceAPI.renderRequestTemplate(
                context,
                paymentState.required_action,
            );
            cybersourceAPI.submitRequest(request);
        },
        "bluefin": () => {
            throw buildCheckoutError(
                "payment",
                "Bluefin should never be pending",
            );
        },
    };
    return handlers;
};

/**
 * Complete order payment after initial order placement.
 *
 * This thunk handles the payment state checking and authorization flow
 * after an order has been placed. It is called:
 * 1. After initial placeOrder to check payment states
 * 2. After Cybersource 3DS iframe completes to finalize payment
 *
 * Unlike placeOrder, this does NOT create a new order - it only checks
 * existing payment states and handles authorization flows.
 *
 * @param {IUser | null} args.user - Current user (or null for guest checkout)
 * @returns {Promise<void>} Promise resolving when payment is complete or flow initiated
 *
 * @throws {ICheckoutHTTPError} If payment is declined
 *
 * @sideEffects
 * - Makes API call to check payment states via checkoutAPI.checkPaymentStates
 * - Updates `data.payment_states` via updatePaymentStates action
 * - May trigger Cybersource 3DS flow via cybersourceAPI.submitRequest
 * - May redirect to thank you page via forwardToThankYouPage
 */
export const completeOrderPayment = createAsyncThunk(
    "checkout/completeOrderPayment",
    async (
        args: CompleteOrderPaymentArgs,
        { getState, dispatch },
    ): Promise<void> => {
        const state = getState() as { checkout: IReduxState };
        const checkoutState = state.checkout;

        // Check payment states and handle authorization
        const handlers = getPaymentHandlers(checkoutState, args.user);
        const paymentMethods = Object.keys(
            checkoutState.form.payment_methods,
        ).map((key) => {
            return checkoutState.form.payment_methods[key].method_type;
        });
        const states = await checkoutAPI.checkPaymentStates();

        if (
            states.order_status === OrderStatus.AUTHORIZED ||
            states.order_status === OrderStatus.MANUAL_REVIEW ||
            states.payment_method_states[PRIMARY_PAYMENT_METHOD_KEY]?.status ===
                PaymentState.DEFERRED
        ) {
            forwardToThankYouPage();
            return;
        }

        // Import actions dynamically to avoid circular dependency
        const { updatePaymentStates } = await import("../reducers");
        dispatch(updatePaymentStates(states));

        let highestAmount: number | null = null;
        let nextMethodKeyToCharge: string | null = null;

        // Check for decline
        objectKeys(states.payment_method_states).forEach((key) => {
            const paymentMethodState = states.payment_method_states[key];
            const paymentMethodType =
                checkoutState.form.payment_methods[key].method_type;

            // Match the current payment method to the corresponding paymentMethodState
            if (
                paymentMethods.includes(paymentMethodType) &&
                paymentMethodState.status === PaymentState.DECLINED
            ) {
                throw buildCheckoutError(
                    "global",
                    PAYMENT_REJECT_MSGS[`${paymentMethodType}`],
                );
            }

            // While we're looping, track highest pending amount
            if (
                paymentMethodState.status === PaymentState.PENDING &&
                (highestAmount === null ||
                    Number(paymentMethodState.amount) > highestAmount)
            ) {
                nextMethodKeyToCharge = key;
                highestAmount = Number(paymentMethodState.amount);
            }
        });

        // Try highest pending amount
        if (nextMethodKeyToCharge) {
            const paymentMethodState =
                states.payment_method_states[nextMethodKeyToCharge];
            const paymentMethodType =
                checkoutState.form.payment_methods[nextMethodKeyToCharge]
                    .method_type;
            const handler = handlers[paymentMethodType];
            handler(nextMethodKeyToCharge, paymentMethodState);
        }
    },
);
